import { useCallback, useEffect, useState } from 'react';
import {
  FeatureListItemDto,
  FieldSettingEditState,
  UserRoleKey,
  FieldPermissionKey,
  SaveFieldPermissionDto,
} from '../../types';
import { AVAILABLE_ROLES } from '../../constants/roles';

import { resolveEffectiveValue } from '../../helpers/fieldHelpers';
import { handleNotifications } from '../../../../../../../generic/utils/handleNotifications';
import { UI_FORMS } from '../../../../../../../modules/API/UIForms/uiform';
import useDataFetcher from '../../../../knisot/hooks/useFetchData';

const useFeatureControl = () => {
  const [selectedFeature, setSelectedFeature] = useState<FeatureListItemDto | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserRoleKey | null>(null);
  const [fieldSettings, setFieldSettings] = useState<FieldSettingEditState[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 1. Fetch features list on mount
  const {
    data: features,
    isLoading: isLoadingFeatures,
  } = useDataFetcher(UI_FORMS.getFeatures, "data");

  // 2. Select feature → reset role and fields
  const handleSelectFeature = useCallback((feature: FeatureListItemDto) => {
    setSelectedFeature(feature);
    setSelectedRole(null);
    setFieldSettings([]);
  }, []);

  // 3. Select role → fetch field settings
  const handleSelectRole = useCallback(
    async (roleKey: UserRoleKey) => {
      if (!selectedFeature) return;

      setSelectedRole(roleKey);
      setIsLoading(true);
      try {
        const response = await UI_FORMS.getFieldSettings(selectedFeature.formId, [roleKey]);
        const withDirtyFlag: FieldSettingEditState[] = response.fields.map((field) => ({
          ...field,
          effectiveIsRequired: resolveEffectiveValue(field.isRequiredOverride, field.defaultIsRequired),
          effectiveIsEditable: resolveEffectiveValue(field.isEditableOverride, field.defaultIsEditable),
          isDirty: false,
        }));
        setFieldSettings(withDirtyFlag);
      } catch {
        handleNotifications('שגיאה בטעינת הגדרות שדות', { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [selectedFeature],
  );

  // 4. Handle toggle change on a specific field
  const handleFieldChange = useCallback(
    (fieldId: number, key: FieldPermissionKey, value: boolean | null) => {
      setFieldSettings((prev) =>
        prev.map((field) => {
          if (field.fieldId !== fieldId) return field;

          const updated = { ...field, [key]: value, isDirty: true };

          // Recalculate effective values
          updated.effectiveIsRequired = resolveEffectiveValue(
            updated.isRequiredOverride,
            updated.defaultIsRequired,
          );
          updated.effectiveIsEditable = resolveEffectiveValue(
            updated.isEditableOverride,
            updated.defaultIsEditable,
          );

          return updated;
        }),
      );
    },
    [],
  );

  // 5. Save dirty fields
  const handleSave = useCallback(async () => {
    if (!selectedFeature || !selectedRole) return;

    const dirtyFields = fieldSettings.filter((f) => f.isDirty);
    if (dirtyFields.length === 0) {
      handleNotifications('אין שינויים לשמירה', { type: 'warning' });
      return;
    }
    const payload: SaveFieldPermissionDto[] = dirtyFields.map((f) => ({
      formId: selectedFeature.formId,
      roleKey: selectedRole,
      fieldId: f.fieldId,
      canView: f.canView,
      isRequiredOverride: f.isRequiredOverride ?? f.defaultIsRequired,
      isEditableOverride: f.isEditableOverride ?? f.defaultIsEditable,
    }));
    console.log(payload)
    setIsLoading(true);
    try {
      await UI_FORMS.changeFieldSettings(payload);
      // Reset dirty flags after successful save
      setFieldSettings((prev) =>
        prev.map((field) => ({ ...field, isDirty: false })),
      );
      handleNotifications('ההגדרות נשמרו בהצלחה', { type: 'success' });
    } catch {
      handleNotifications('שגיאה בשמירת ההגדרות', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [selectedFeature, selectedRole, fieldSettings]);

  const hasDirtyFields = fieldSettings.some((f) => f.isDirty);

  return {
    features: features?.features || [],
    selectedFeature,
    handleSelectFeature,
    roles: AVAILABLE_ROLES,
    selectedRole,
    handleSelectRole,
    fieldSettings,
    handleFieldChange,
    handleSave,
    isLoading: isLoadingFeatures || isLoading,
    hasDirtyFields,
  };
};

export default useFeatureControl;
